import { FC, useEffect, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { Toggle } from "@/components/Interface/Toggle/Toggle";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useGetCustomer, useUpdateCustomer } from "@/services/api";
import { LookupValue, PermissionPermissionType } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

// TODO: combine with VoiceSettings logic
// The dropdown lists English, German, French in this order.
export const LANGUAGES: LookupValue[] = [
  { id: 2, description: "english" },
  { id: 1, description: "german" },
  { id: 3, description: "french" },
];

const displayLanguage = (t: (key: string) => string, language?: LookupValue) =>
  language && (language.id === 1 || language.id === 2 || language.id === 3)
    ? t(`portal:user-account.inbox.languages.${language.id}`)
    : t("portal:user-account.inbox.noLanguageSet");

export const Inbox: FC = () => {
  const { t } = useTranslation();
  const showAlert = useAlert();
  const [mode, setMode] = useQueryParam("mode");
  const isChangingLanguage = mode === "edit";

  const {
    data: customer,
    isLoading: customerIsLoading,
    refetch,
  } = useGetCustomer();
  const { mutate: updateCustomer, isLoading: mutationIsLoading } =
    useUpdateCustomer();

  const [selectedLanguage, setSelectedLanguage] = useState<
    LookupValue | undefined
  >(customer?.language);

  useEffect(() => {
    // Only resync from the server while the user isn't mid-edit — otherwise
    // an unrelated refetch (window focus, the marketing toggle's mutation)
    // can silently discard a language the user already picked but hasn't
    // saved yet. Prefer the canonical LANGUAGES entry so Combobox's
    // referential equality highlights the right option.
    if (!isChangingLanguage) {
      const match = LANGUAGES.find((l) => l.id === customer?.language?.id);
      setSelectedLanguage(match ?? customer?.language);
    }
  }, [customer?.language, isChangingLanguage]);

  const isMarketingActive = !!customer?.permissions?.some(
    ({ permissionType, accepted }) =>
      permissionType === PermissionPermissionType.MARKETING && accepted,
  );

  const toggleMarketing = () => {
    if (!customer?.id || !customer?.customerId) {
      return;
    }
    updateCustomer(
      {
        data: {
          customerId: customer.customerId,
          id: customer.id,
          language: customer.language,
          permissions: [
            {
              permissionType: PermissionPermissionType.MARKETING,
              accepted: !isMarketingActive,
            },
          ],
        },
      },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          showAlert({
            type: AlertTypes.error,
            text: t("portal:user-account.inbox.saveError"),
          });
        },
      },
    );
  };

  const saveLanguage = () => {
    if (!customer?.id || !customer?.customerId) {
      return;
    }
    updateCustomer(
      {
        data: {
          customerId: customer.customerId,
          id: customer.id,
          language: selectedLanguage,
          permissions: customer.permissions,
        },
      },
      {
        onSuccess: () => {
          refetch();
          setMode("", { replace: true });
        },
        onError: () => {
          showAlert({
            type: AlertTypes.error,
            text: t("portal:user-account.inbox.saveError"),
          });
        },
      },
    );
  };

  const cancelLanguageChange = () => {
    setSelectedLanguage(customer?.language);
    setMode("", { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      {customer === undefined || customerIsLoading ? (
        <LoadingSpinner />
      ) : (
        <TwoColumnLayout
          rows={[
            {
              left: (
                <div className="flex w-full flex-col gap-6">
                  <ProfileCard
                    title={t("portal:user-account.inbox.contactlanguage")}
                    stickyHeader={isChangingLanguage}
                    maxWidth="none"
                    actions={
                      isChangingLanguage ? (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            onClick={cancelLanguageChange}
                          >
                            {t("portal:user-account.security.buttons.cancel")}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            type="button"
                            disabled={mutationIsLoading}
                            onClick={saveLanguage}
                          >
                            {t("portal:user-account.security.buttons.save")}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          type="button"
                          onClick={() => setMode("edit", { replace: true })}
                        >
                          {t("portal:user-account.personalDetails.change")}
                        </Button>
                      )
                    }
                  >
                    {/* Always show the Combobox (same as Personal Details
                        country/legal-form fields): disabled in view mode,
                        editable once the user hits ändern. */}
                    <Combobox<LookupValue>
                      className="w-full"
                      layout="stacked"
                      dataTestId="languagesCombobox"
                      label={t("Common.language")}
                      displayValue={(language) => displayLanguage(t, language)}
                      options={LANGUAGES}
                      placeholder={t("Common.language")}
                      disabled={!isChangingLanguage}
                      field={
                        {
                          name: "language",
                          value: selectedLanguage,
                          onChange: setSelectedLanguage,
                          onBlur: () => undefined,
                          ref: () => undefined,
                        } as ControllerRenderProps
                      }
                    />
                  </ProfileCard>

                  <ProfileCard
                    title={t("portal:user-account.inbox.marketingTitle")}
                    description={t(
                      "portal:user-account.inbox.marketingSubtitle",
                    )}
                    stickyHeader={false}
                    maxWidth="none"
                    actions={
                      <Toggle
                        enabled={isMarketingActive}
                        onChange={toggleMarketing}
                        disabled={mutationIsLoading || isChangingLanguage}
                        ariaLabel={t(
                          "portal:user-account.inbox.marketingTitle",
                        )}
                        size="sm"
                      />
                    }
                  />
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};
